<template>
	<NuxtLayout
		name="hero-diagonal"
		:background-video="{
			mp4Src: cdnUrl('fit-hier-homepage-header_1080p_v1.mp4'),
			webmSrc: cdnUrl('fit-hier-homepage-header_1080p_v1.webm'),
			posterImg: {
				src: '/img/fitness/small-group-training_01.jpg',
				alt: 'Fit Hier Small Group Training',
				preload: true,
				width: 1920,
				height: 1490,
			},
		}"
		page-title-prefix="Small Group Training"
		page-title="Jouw privé sportschool"
		:breadcrumbs
	>
		<div class="flex flex-col gap-36 pb-36 md:pt-64">
			<div class="flex flex-col gap-36 sticky">
				<div class="lg:container flex flex-col md:grid md:grid-cols-3 gap-12">
					<div>
						<div class="sticky top-36 flex flex-col gap-8 px-12 md:px-0">
							<AtomCard class="p-12 flex flex-col gap-12 rounded-lg">
								<div class="flex flex-col gap-4">
									<AtomTitle
										tag="h2"
										size="xl"
									>
										Small Group Training
									</AtomTitle>
									<p class="text-functional-size-lg text-gray-600">
										Waarom Hier?
									</p>
								</div>
								<MoleculeIconList
									:items="[
										'Trainen in een privé setting',
										'Gevarieerd trainingsprogramma',
										'Maximaal 6 personen per training',
										'Uitdagend voor iedereen',
										'Aangepast op jouw niveau',
										'Volledig ontzorgd trainen',
										'Train met een gemotiveerde groep',
										'Gratis gebruik van handdoeken',
										'Gratis gebruik van de koffiebar',
									]"
								/>
							</AtomCard>
							<AtomButton
								size="lg"
								text="Informatie aanvragen"
								:variant="ButtonVariant.Green"
								icon-left="fa-envelope"
								:to="{ url: '#aanmelden', title: 'Small Group Training informatie aanvragen' }"
								@click="trackButtonClick('SGTPage_Intro_CTA_Lead')"
							/>
							<AtomButton
								size="lg"
								text="Doe een keer gratis mee"
								icon-left="fa-bolt"
								:to="{ title: 'Aanmelden voor een Small Group Training proefles', url: '/small-group-training/aanmelden-proefles' }"
								@click="trackButtonClick('SGTPage_Intro_CTA_Trial')"
							/>
						</div>
					</div>
					<div class="relative col-span-2 grid gap-12">
						<div class="col-span-2 grid gap-12 bg-gray-50 md:rounded-lg p-12">
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Waarom trainen met Small Group Training?
								</AtomTitle>
								<p class="text-paragraph-size-lg">
									Small Group Training combineert de persoonlijke aandacht van een personal trainer met de energie van een groep. Je traint met maximaal zes personen tegelijk, onder begeleiding van een deskundige coach. Het resultaat: effectiever trainen, meer motivatie en zichtbaar resultaat — zonder te verdwalen in een volle sportschool.
								</p>
							</div>
							<AtomTitle
								tag="h3"
								size="xl"
								class="mt-8"
							>
								De 3 pijlers van onze trainingen
							</AtomTitle>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Kracht
								</AtomTitle>
								<p>
									Sterker worden maakt alles makkelijker — van boodschappen tillen tot blessurevrij bewegen. In onze trainingen werk je aan functionele kracht: bewegingen die je dagelijks gebruikt. Denk aan tillen, duwen en stabiliseren. Onze trainers zorgen voor techniek, progressie en veiligheid. Kracht is de basis voor een fit, veerkrachtig lichaam.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Conditie
								</AtomTitle>
								<p>
									Een goede conditie zorgt voor energie, uithoudingsvermogen en een fitter gevoel. We combineren intensieve blokken met uitdagende oefeningen die je hartslag verhogen en vetverbranding stimuleren. Je bouwt op je eigen niveau en merkt al snel verschil. Geen saaie cardio, maar afwisselende trainingen die écht werken.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h4"
									size="lg"
								>
									Mobiliteit
								</AtomTitle>
								<p>
									Soepel en gecontroleerd bewegen voorkomt klachten en verbetert je prestaties. Daarom besteden we in elke training aandacht aan mobiliteit: van actieve warming-up tot gerichte stretching. Je leert beter bewegen, met meer controle en minder beperkingen. Meer mobiliteit betekent meer vrijheid in je lijf.
								</p>
							</div>
						</div>
					</div>
				</div>

				<div class="container">
					<div class="flex flex-col gap-12">
						<AtomTitle
							tag="h2"
							size="3xl"
						>
							Small Group Training lesaanbod
						</AtomTitle>
						<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
							<MoleculeSquareCard
								title="Functional Conditioning"
								text="Deze training draait om uithoudingsvermogen, explosiviteit en algemene fitheid. We combineren functionele bewegingen met cardio-elementen om je hartslag omhoog te brengen. Perfect als je wilt werken aan je conditie, vetverbranding en energieniveau."
								:image="{
									src: '/img/fitness/small-group-training_03.jpg',
									alt: 'Slee workout',
									width: 800,
									height: 800,
								}"
								:button="{
									text: 'Meer over Conditioning training',
									to: {
										url: '/small-group-training/functional-conditioning-conditietraining',
										title: 'Functional Conditioning',
									},
								}"
							/>
							<MoleculeSquareCard
								title="Functional Strength"
								text="In deze les ligt de focus op kracht en spieropbouw met functionele oefeningen. Ontwikkel niet alleen kracht, maar ook controle en stabiliteit. Ideaal voor wie sterker en blessurebestendiger wil worden."
								:image="{
									src: '/img/fitness/slee_06.jpg',
									alt: 'Slee workout',
									width: 800,
									height: 800,
								}"
								:button="{
									text: 'Meer over Strength training',
									to: {
										url: '/small-group-training/functional-strength-krachttraining',
										title: 'Functional Strength',
									},
								}"
							/>
							<MoleculeSquareCard
								title="LifeFit"
								text="LifeFit is onze meest toegankelijke training, gericht op soepel bewegen, kracht in het dagelijks leven en plezier in sport. Met een mix van mobiliteit, lichte kracht en coördinatieoefeningen is deze les perfect voor elk niveau."
								:image="{
									src: '/img/fitness/small-group-training_02.jpg',
									alt: 'Slee workout',
									width: 800,
									height: 800,
								}"
								:button="{
									text: 'Meer over LifeFit training',
									to: {
										url: '/small-group-training/lifefit-functionele-begeleide-fitness',
										title: 'LifeFit',
									},
								}"
							/>
						</div>
					</div>
					<div class="flex flex-col gap-6 mt-20">
						<AtomTitle
							tag="h3"
							size="lg"
						>
							Benieuwd naar de wekelijkse tijden voor Small Group Training?
						</AtomTitle>
						<AtomLink
							url="/small-group-training/trainingsschema-en-tijden"
							title="Small Group Training lesrooster"
							class="flex items-center gap-4 link text-purple-700"
						>
							<span class="font-medium">Check het Small Group Training lesrooster</span>
							<AtomFontAwesome icon="fa-arrow-right" />
						</AtomLink>
					</div>
				</div>
			</div>

			<MoleculeBackgroundSection
				id="aanmelden"
				color="purple"
				class="lg:scroll-mt-72"
			>
				<div class="flex flex-col items-center gap-12">
					<AtomTitle
						tag="h2"
						size="3xl"
					>
						Small Group Training informatie aanvragen
					</AtomTitle>
					<div class="lg:w-3/4 flex flex-col gap-12">
						<p class="text-center">
							Wil je meer weten over Small Group Training bij Fit Hier? Vraag het informatiepakket aan met alle details over de trainingen, het rooster en de tarieven. Je ontvangt binnen een paar minuten een mail met alle informatie.
						</p>
						<AtomCard class="p-12 flex flex-col gap-12 rounded-lg text-black">
							<OrganismFormLeadSgt />
						</AtomCard>
					</div>
				</div>
			</MoleculeBackgroundSection>

			<div class="container flex flex-col gap-32 md:gap-64 md:py-32">
				<CompositionActionButtonsSgt1 />
				<div class="flex flex-col gap-12 items-center">
					<AtomTitle
						tag="h2"
						size="3xl"
						class="text-center"
					>
						Onze leden aan het woord
					</AtomTitle>

					<OrganismReviewSlideshow :reviews="reviews" />
				</div>
			</div>

			<div class="container max-w-screen-md flex flex-col gap-12 pb-36">
				<AtomTitle
					tag="h2"
					size="3xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq :items="sgtFaqItems" />
			</div>

			<div class="container py-24">
				<div class="text-center md:text-left grid grid-cols-1 md:grid-cols-3 gap-24 md:gap-36">
					<div class="flex flex-col gap-12 justify-center">
						<LazyAtomTitle
							tag="h2"
							size="xl"
						>
							Een kijkje in onze trainingen
						</LazyAtomTitle>
						<p class="text-gray-600">
							Benieuwd hoe Small Group Training bij Fit Hier eruitziet? In deze video krijg je een indruk van de sfeer, de oefeningen en de begeleiding. Zo weet je precies wat je kunt verwachten en waarom trainen bij ons zo effectief én leuk is.
						</p>
					</div>
					<AtomVideo
						class="rounded md:col-span-2"
						:mp4-src="cdnUrl('small-group-training.mp4')"
						:webm-src="cdnUrl('small-group-training.webm')"
						:poster-img="{
							src: '/img/video-posters/small-group-training.png',
							alt: 'Small Group Training',
							preload: true,
							width: 800,
							height: 450,
						}"
						controls
						preload="none"
					/>
				</div>
			</div>
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import { cdnUrl } from '#base/helpers/contentHelpers';
import reviews from '#fit/core/consts/reviews';
import { sgtFaqItems } from '#fit/core/consts/faqItems';
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Small Group Training',
		to: {
			url: '/small-group-training',
			title: 'Small Group Training',
		},
	},
];

useSeoMeta({
	title: 'Small Group Training Uden - Max 6 deelnemers | Fit Hier',
	ogTitle: 'Small Group Training in Uden, kleine groep, max 6 deelnemers',
	description: 'Ervaar motiverende Small Group Training in Uden. Maximaal 6 deelnemers, mix van kracht en conditie, persoonlijke coaching. Boek een gratis proefles!',
});

const { trackButtonClick } = useEventTracking();
</script>
